import {
  BuildFilled,
  ClockCircleOutlined,
  CrownOutlined,
  EnvironmentFilled,
  EnvironmentOutlined,
  EyeInvisibleOutlined,
  HeartFilled,
  HistoryOutlined,
  IdcardFilled,
  LaptopOutlined,
  LinkOutlined,
  ReadOutlined,
  TeamOutlined,
  ToolFilled,
  TrophyOutlined,
  WarningOutlined,
} from '@ant-design/icons'
import { Descriptions, Divider, Typography } from 'antd'
import cls from 'classnames'
import moment from 'moment'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { UserAvatar, UserHoverCard } from '@/components'
import {
  ParentCommentsFragment,
  PostType,
  RequestedPostFragment,
  useGetNewAgoranCardInfoQuery,
} from '@/generated/graphql'
import { ContentModeContext } from '@/modules/user/user-timeline/user-timeline-context'
import { routes } from '@/routes'
import { Cities } from '@/shares/i18n-mapping'

import s from '../post-card.module.less'

export const SpecialTemplate: React.FC<{
  post: RequestedPostFragment & ParentCommentsFragment
  isRecommended: boolean
  disableBackground?: boolean
}> = ({ post, isRecommended, disableBackground = false }) => {
  const specialText = React.useMemo(() => {
    switch (post.postType!) {
      case 'Thanks':
        return s.thanksText
      case 'Forum':
        return s.forumText
      default:
        return s.introText
    }
  }, [post.postType])

  const mode = React.useContext(ContentModeContext)

  const imagePostTypes = [PostType.Forum, PostType.Thanks]
  const specialImage = (() => {
    if (new Date().getMonth() === 11 && Math.abs(25 - new Date().getDate()) < 5) {
      return s.christmasImage
    }
    const today = new Date()
    switch (post.postType!) {
      case 'Thanks':
        return post.receiver?.gender === '2' && today.getMonth() === 2 && today.getDate() >= 5 && today.getDate() <= 17
          ? s.womenDayImage
          : s.thanksImage
      case 'Forum':
        return s.forumImage
      default:
        return null
    }
  })()

  const specialContent = (() => {
    switch (post.postType!) {
      case 'Event':
        return <EventTemplate post={post} isRecommended={isRecommended} />
      case 'Honor':
        return <HonorTemplate post={post} isRecommended={isRecommended} />
      case 'Introduction':
        return <NewAgoranTemplate post={post} />
      default:
        return (
          <div className={cls(s.specialText, specialText, isRecommended ? s.recommendedText : s.normalText)}>
            {post.content.slice(0, mode.wordLength)}
          </div>
        )
    }
  })()

  return (
    <>
      {specialContent}
      {imagePostTypes.includes(post.postType) && (
        <div className={disableBackground ? undefined : cls(s.specialImage, specialImage)} />
      )}
    </>
  )
}

const NewAgoranTemplate: React.FC<{ post: RequestedPostFragment & ParentCommentsFragment }> = ({ post }) => {
  const { t } = useTranslation()

  const { data } = useGetNewAgoranCardInfoQuery({
    fetchPolicy: 'network-only',
    variables: {
      userId: post.creator!.userId,
    },
  })

  if (!data) {
    return null
  }

  const newAgoran = data.user
  const skills = newAgoran.skills?.edges.map(({ node }) => node) || []
  const hobbies = newAgoran.hobbies?.edges.map(({ node }) => node) || []
  const bigAvatar = <UserAvatar shape='square' className={s.avatar} size={100} user={newAgoran} />
  const pic = (
    <div className={s.pic}>
      {bigAvatar}
      <span className={s.name}>{newAgoran?.name}</span>
    </div>
  )

  const theInfo = (
    <Descriptions column={1} labelStyle={{ fontSize: 14, color: '#707070', fontWeight: 300 }}>
      <Descriptions.Item
        label={
          <span>
            <BuildFilled />
            <span className={s.label}>{t('profile.company.departmentName')}</span>
          </span>
        }
      >
        {newAgoran.departments.map(({ name }) => name)[0]}
      </Descriptions.Item>
      <Descriptions.Item
        label={
          <span>
            <ToolFilled />
            <span className={s.label}>{t('profile.basic.skillSet')}</span>
          </span>
        }
      >
        {skills.map((skill) => skill.name).join(', ')}
      </Descriptions.Item>
      <Descriptions.Item
        label={
          <span>
            <HeartFilled />
            <span className={s.label}>{t('profile.basic.hobbies')}</span>
          </span>
        }
      >
        {hobbies.map((hobby) => hobby.name).join(', ')}
      </Descriptions.Item>
      <Descriptions.Item
        label={
          <span>
            <EnvironmentFilled />
            <span className={s.label}>{t('profile.company.location')}</span>
          </span>
        }
      >
        {t(Cities[newAgoran.companyProfile.location.value])}
      </Descriptions.Item>
    </Descriptions>
  )

  const aboutMe = (
    <div className={s.aboutMe}>
      <Descriptions column={1} labelStyle={{ fontSize: 14, color: '#707070', fontWeight: 300 }}>
        <Descriptions.Item
          label={
            <span>
              <IdcardFilled />
              <span className={s.label}>{t('profile.basic.aboutMe')}</span>
            </span>
          }
        >
          {newAgoran.basicProfile.aboutMe.value ? newAgoran.basicProfile.aboutMe.value : 'TA有点懒，什么都没留下'}
        </Descriptions.Item>
      </Descriptions>
    </div>
  )
  return (
    <div className={s.newAgoranContainer}>
      <div className={s.mainContent}>
        {pic}
        {theInfo}
      </div>
      {aboutMe}
    </div>
  )
}

const EventTemplate: React.FC<{ post: RequestedPostFragment & ParentCommentsFragment; isRecommended: boolean }> = ({
  post,
  isRecommended,
}) => {
  if (!post.event) {
    return null
  }
  const [t] = useTranslation()
  const mode = React.useContext(ContentModeContext)

  const startTime = moment(post.event.startTime).format('YYYY-MM-DD dddd HH:mm')
  const endTime = moment(post.event.endTime).format('YYYY-MM-DD dddd HH:mm')
  const deadline = moment(post.event.deadline).format('YYYY-MM-DD dddd HH:mm')

  const event = (
    <Descriptions
      column={1}
      labelStyle={{ fontSize: 16, color: '#707070', width: 150 }}
      layout={isRecommended ? 'vertical' : 'horizontal'}
      title={
        <span className={s.eventTitle}>
          {post.event.topic}
          {!mode.isSimplified && <Divider />}
        </span>
      }
    >
      {!mode.isSimplified && (
        <>
          {post.event.guests.length > 0 && (
            <Descriptions.Item
              label={
                <span>
                  <CrownOutlined /> {t('post.event.guests')}
                </span>
              }
            >
              {post.event.guests.map((g) => (
                <UserHoverCard key={g.user.userId} email={g.user.email}>
                  <Link
                    onClick={(e) => e.stopPropagation()}
                    key={g.user.userId}
                    style={{ marginRight: 8 }}
                    to={routes.users.page(g.user.email)}
                  >
                    {g.user.name}
                  </Link>
                </UserHoverCard>
              ))}
            </Descriptions.Item>
          )}
          {post.event.foreignGuests && (
            <Descriptions.Item
              label={
                <span>
                  <CrownOutlined /> {t('post.event.foreignGuests')}
                </span>
              }
            >
              {post.event.foreignGuests}
            </Descriptions.Item>
          )}
          <Descriptions.Item
            label={
              <span>
                <ReadOutlined /> {t('post.event.content')}
              </span>
            }
          >
            <span className={s.eventContent}>{post.event.content}</span>
          </Descriptions.Item>
          <Descriptions.Item
            label={
              <span>
                <ClockCircleOutlined /> {t('post.event.startTime')}
              </span>
            }
          >
            {startTime}
          </Descriptions.Item>
          <Descriptions.Item
            label={
              <span>
                <ClockCircleOutlined /> {t('post.event.endTime')}
              </span>
            }
          >
            {endTime}
          </Descriptions.Item>
          <Descriptions.Item
            label={
              <span>
                <EnvironmentOutlined /> {t('post.event.location')}
              </span>
            }
          >
            {post.event.location}
          </Descriptions.Item>
          {post.event.openPosition && (
            <Descriptions.Item
              label={
                <span>
                  <TeamOutlined className={s.warning} /> {t('post.event.openPosition')}
                </span>
              }
            >
              {post.event.openPosition}
            </Descriptions.Item>
          )}
          {post.event.restriction && (
            <Descriptions.Item
              label={
                <span>
                  <WarningOutlined className={s.warning} /> {t('post.event.restriction')}
                </span>
              }
            >
              {post.event.restriction}
            </Descriptions.Item>
          )}
          {post.event.deadline && (
            <Descriptions.Item
              label={
                <span>
                  <HistoryOutlined className={s.warning} /> {t('post.event.deadline')}
                </span>
              }
            >
              {deadline}
            </Descriptions.Item>
          )}
          {post.event.avcRoom && (
            <Descriptions.Item
              label={
                <span>
                  <LaptopOutlined className={s.avc} /> {t('post.event.avcRoom')}
                </span>
              }
            >
              {post.event.avcRoom}
              <Typography.Text copyable={{ text: post.event.avcRoom }} />
            </Descriptions.Item>
          )}
          {post.event.avcRoom && (
            <Descriptions.Item
              label={
                <span>
                  <EyeInvisibleOutlined className={s.avc} /> {t('post.event.avcPassword')}
                </span>
              }
            >
              {post.event.avcPassword}
              {post.event.avcPassword && <Typography.Text copyable={{ text: post.event.avcPassword }} />}
            </Descriptions.Item>
          )}
          {post.event.avcLink && (
            <Descriptions.Item
              label={
                <span>
                  <LinkOutlined className={s.avc} /> {t('post.event.avcLink')}
                </span>
              }
            >
              <a href={post.event.avcLink} rel='noopener noreferrer' target='_blank'>
                {post.event.avcLink}
              </a>
            </Descriptions.Item>
          )}
        </>
      )}
    </Descriptions>
  )

  return (
    <div
      className={cls(
        s.eventContainer,
        isRecommended ? s.recommendedWidth : s.normalWidth,
        isRecommended ? s.recommendedCase : null
      )}
    >
      {event}
    </div>
  )
}

const HonorTemplate: React.FC<{ post: RequestedPostFragment & ParentCommentsFragment; isRecommended: boolean }> = ({
  post,
  isRecommended,
}) => {
  if (!post.honor) {
    return null
  }
  const [t] = useTranslation()
  const mode = React.useContext(ContentModeContext)

  const honor = (
    <Descriptions
      column={1}
      labelStyle={{ fontSize: 16, color: '#707070', width: 120 }}
      layout={isRecommended ? 'vertical' : 'horizontal'}
      title={<h2 className={s.honorTitle}>{post.honor.title}</h2>}
    >
      {!mode.isSimplified && (
        <>
          <Descriptions.Item
            label={
              <span className={s.honored}>
                <TrophyOutlined />
                {t('post.honor.modal.commendation')}
              </span>
            }
          >
            <span>{post.honor.commendation}</span>
          </Descriptions.Item>
          {post.honor.honoredProjects && (
            <Descriptions.Item
              label={
                <span className={s.honored}>
                  <TrophyOutlined />
                  {t('post.honor.modal.honoredProjects')}
                </span>
              }
            >
              <span>{post.honor.honoredProjects}</span>
            </Descriptions.Item>
          )}
          {post.honor.honoredSignificants && post.honor.honoredSignificants?.length > 0 && (
            <Descriptions.Item
              label={
                <span className={s.honored}>
                  <TrophyOutlined />
                  {t('post.honor.modal.honoredSignificants')}
                </span>
              }
            >
              {post.honor.honoredSignificants.map((g) => (
                <UserHoverCard key={g.user.userId} email={g.user.email}>
                  <Link
                    onClick={(e) => e.stopPropagation()}
                    key={g.user.userId}
                    style={{ marginRight: 8 }}
                    to={routes.users.page(g.user.email)}
                  >
                    {g.user.name}
                  </Link>
                </UserHoverCard>
              ))}
            </Descriptions.Item>
          )}
          <Descriptions.Item
            label={
              <span className={s.honored}>
                <TrophyOutlined />
                {t('post.honor.modal.honoredIndividuals')}
              </span>
            }
          >
            {post.honor.honoredIndividuals.map((g) => (
              <UserHoverCard key={g.user.userId} email={g.user.email}>
                <Link
                  onClick={(e) => e.stopPropagation()}
                  key={g.user.userId}
                  style={{ marginRight: 8 }}
                  to={routes.users.page(g.user.email)}
                >
                  {g.user.name}
                </Link>
              </UserHoverCard>
            ))}
          </Descriptions.Item>
        </>
      )}
    </Descriptions>
  )

  return (
    <div
      className={cls(
        s.eventContainer,
        isRecommended ? s.recommendedWidth : s.normalWidth,
        isRecommended ? s.recommendedCase : null
      )}
    >
      {honor}
    </div>
  )
}
